import {RaffleASchema} from '@cohort/admin-schemas/perk';
import {PerkIntegrationIdSchema} from '@cohort/shared/schema/apps';
import {asArray, DateSchema, EmailSchema} from '@cohort/shared/schema/common';
import {NetworkSchema} from '@cohort/shared/schema/common/blockchain';
import {PaginationParamsSchema} from '@cohort/shared/schema/common/pagination';
import {
  PerkAccessStatusSchema,
  PerkStatusSchema,
  PerkTypeSchema,
} from '@cohort/shared/schema/common/perks';
import {z} from 'zod';

export const PerkAnalyticsUsageASchema = z.object({
  perkUsageId: z.string().uuid(),
  perkAccessId: z.string().uuid(),
  perkType: PerkTypeSchema,
  perkIntegrationId: PerkIntegrationIdSchema.nullable(),
  data: z.unknown().nullable(),
  integrationConfig: z.unknown().nullable(),
  usedAt: DateSchema,
  userId: z.string().uuid(),
  userEmail: EmailSchema,
});
export type PerkAnalyticsUsageADto = z.infer<typeof PerkAnalyticsUsageASchema>;

export const PerkAnalyticsAccessASchema = z.object({
  perkId: z.string().uuid(),
  perkAccessId: z.string().uuid(),
  digitalAssetId: z.string().uuid(),
  digitalAssetImageKey: z.string(),
  digitalAssetCollectionInternalName: z.string(),
  campaignId: z.string().uuid().nullable(),
  campaignInternalName: z.string().nullable(),
  tokenId: z.number(),
  removed: z.boolean(),
  ownerUserId: z.string().uuid().nullable(),
  ownerUserEmail: EmailSchema.nullable(),
  ownerAddress: z.string().nullable(),
  network: NetworkSchema.nullable(),
  usageCount: z.number(),
  lastUsageDate: DateSchema.nullable(),
  usages: z.array(PerkAnalyticsUsageASchema),
});
export type PerkAnalyticsAccessADto = z.infer<typeof PerkAnalyticsAccessASchema>;

export const PerkAnalyticsOrderByASchema = z.enum([
  'lastUsageDate',
  'usageCount',
  'ownerUserEmail',
  'tokenId',
  '-lastUsageDate',
  '-usageCount',
  '-ownerUserEmail',
  '-tokenId',
]);
export type PerkAnalyticsOrderByADto = z.infer<typeof PerkAnalyticsOrderByASchema>;

export const PerkAnalyticsBaseParamsASchema = z.object({
  collectionIds: z.preprocess(asArray, z.array(z.string().uuid()).optional()),
  orderBy: PerkAnalyticsOrderByASchema.optional(),
  statuses: z.preprocess(asArray, z.array(PerkAccessStatusSchema)).optional(),
});
export type PerkAnalyticsBaseParamsADto = z.infer<typeof PerkAnalyticsBaseParamsASchema>;

export const PerkAnalyticsExportParamsASchema = PerkAnalyticsBaseParamsASchema.merge(
  z.object({
    format: z.enum(['csv']),
  })
);
export type PerkAnalyticsExportParamsADto = z.infer<typeof PerkAnalyticsExportParamsASchema>;

export const PerkAnalyticsParamsASchema = PaginationParamsSchema.merge(
  PerkAnalyticsBaseParamsASchema
);
export type PerkAnalyticsParamsADto = z.infer<typeof PerkAnalyticsParamsASchema>;

export const PerksAnalyticsOrderByASchema = z.enum([
  'name',
  'createdAt',
  'updatedAt',
  'type',
  '-name',
  '-createdAt',
  '-updatedAt',
  '-type',
]);
export type PerksAnalyticsOrderByADto = z.infer<typeof PerksAnalyticsOrderByASchema>;

export const PerksAnalyticsOverviewASchema = z.object({
  perkId: z.string().uuid(),
  name: z.string(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  type: PerkTypeSchema,
  integration: z
    .object({
      perkIntegrationId: PerkIntegrationIdSchema,
    })
    .nullable(),
  status: PerkStatusSchema,
  raffle: RaffleASchema.nullable(),
  bannerFileKey: z.string().nullable(),
  thumbnailFileKey: z.string().nullable(),
  collectionsCounter: z.number().int().min(0),
  digitalAssetHoldersWithPerkUsage: z.number().int().min(0),
  digitalAssetHoldersTotal: z.number().int().min(0),
});
export type PerksAnalyticsOverviewADto = z.infer<typeof PerksAnalyticsOverviewASchema>;

export const PerksAnalyticsOverviewParamsASchema = PaginationParamsSchema.merge(
  z.object({
    orderBy: PerksAnalyticsOrderByASchema,
    statuses: z.preprocess(asArray, z.array(PerkStatusSchema).optional()),
    integrations: z.preprocess(asArray, z.array(PerkIntegrationIdSchema).optional()),
    types: z.preprocess(asArray, z.array(PerkTypeSchema).optional()),
  })
);
export type PerksAnalyticsOverviewParamsADto = z.infer<typeof PerksAnalyticsOverviewParamsASchema>;
